import { useEffect, useMemo, useRef, useState } from "react";
import type { PublicConventionGuide } from "@verity-connect/contracts";
import { Link } from "./router";
import { personalWorkspaceName } from "./association-presentation";

/**
 * The sessions she sat in, as WVCEA recorded them.
 *
 * She hears the session's kept recording rather than her own file, because a seminar is one
 * room and the association keeps one copy of it. A teacher whose own recording lost to a
 * longer one still sat through the seminar, and so did a teacher who only scanned the square
 * at the door.
 *
 * One player, pinned at the top, rather than a player inside every row. Two players on a
 * page is two people talking at once, and a teacher who scrolls while listening should not
 * lose the controls.
 */

interface HerRecording {
  id: string;
  itemId: string;
  title: string;
  startsAt: string | null;
  seconds: number;
  bytes: number;
  hasTranscript: boolean;
  because: readonly string[];
}

/** The speeds people actually use for a person talking, and nothing in between. */
const SPEEDS = [1, 1.25, 1.5, 1.75, 2] as const;

export function MyRecordings({ guide, signedIn }: { guide: PublicConventionGuide; signedIn: boolean }) {
  const [recordings, setRecordings] = useState<HerRecording[] | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [speed, setSpeed] = useState<number>(1);
  const [failed, setFailed] = useState(false);
  const audio = useRef<HTMLAudioElement | null>(null);
  const listPath = `/api/attendee/events/${encodeURIComponent(guide.event.slug)}/recordings/mine`;

  useEffect(() => {
    if (!signedIn) { setRecordings([]); return; }
    const controller = new AbortController();
    void fetch(listPath, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => response.ok ? await response.json() as { recordings: HerRecording[] } : null)
      .then((answer) => { if (!controller.signal.aborted) setRecordings(answer?.recordings ?? []); })
      .catch(() => { if (!controller.signal.aborted) { setRecordings([]); setFailed(true); } });
    return () => controller.abort();
  }, [listPath, signedIn]);

  // The rate is set on the element, and a new source resets it, so it is put back each time.
  useEffect(() => { if (audio.current) audio.current.playbackRate = speed; }, [speed, playingId]);

  /**
   * Choosing a recording starts it.
   *
   * Tapping the play button beside a session used to do half the job: it opened the
   * recording and then waited for her to find the smaller play button inside the player and
   * press that too. Two taps to start one thing, the second on a control she had to go
   * looking for.
   *
   * The element is always on the page, even with nothing chosen, so this can set its source
   * and start it inside the tap itself. A phone refuses to play sound a person did not ask
   * for, and asking after the tap has finished is no longer part of the tap.
   */
  const choose = (id: string) => {
    const element = audio.current;
    if (id === playingId) {
      element?.pause();
      setPlayingId(null);
      return;
    }
    setPlayingId(id);
    if (!element) return;
    element.src = audioUrl(guide.event.slug, id);
    element.playbackRate = speed;
    void element.play().catch(() => { /* She can press play herself; nothing is broken. */ });
  };

  const byDay = useMemo(() => {
    const days = new Map<string, HerRecording[]>();
    for (const one of recordings ?? []) {
      const label = dayLabel(one.startsAt, guide);
      const already = days.get(label);
      if (already) already.push(one); else days.set(label, [one]);
    }
    return [...days];
  }, [recordings, guide]);

  const playing = (recordings ?? []).find((one) => one.id === playingId) ?? null;

  if (!signedIn) {
    return (
      <div className="page personal-page">
        <Link className="back-link subpage-back" href="/my-wvcea">← {personalWorkspaceName()}</Link>
        <section className="personal-empty">
          <h1 data-page-heading tabIndex={-1}>Sign in to hear your sessions</h1>
          <p>Your recordings are tied to the seminars you were in, so they have to come from your account.</p>
          <Link className="primary-action" href="/account">Sign in</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="page personal-page my-recordings-page">
      <Link className="back-link subpage-back" href="/my-wvcea">← {personalWorkspaceName()}</Link>
      <section className="page-intro"><h1 data-page-heading tabIndex={-1}>My Recordings</h1></section>

      {/*
        * Always here, hidden until there is something to hear.
        *
        * The player used to be created at the moment a recording was chosen, and an element
        * that does not exist when the finger goes down cannot be started by that finger: a
        * phone will not play sound on its own afterwards. So tapping the play button beside
        * a session opened the recording and then waited for her to find the smaller play
        * button inside and press that too.
        */}
      <section
        className={playing ? "recording-player" : "recording-player is-quiet"}
        aria-label={playing ? `Playing ${playing.title}` : undefined}
        aria-hidden={playing ? undefined : true}
      >
        <p className="recording-player-eyebrow">Now playing</p>
        <h2>{playing?.title ?? ""}</h2>
        <p className="recording-player-meta">{playing ? whenLabel(playing.startsAt, guide) : ""}</p>
        {/* The browser's own controls: a scrubber, a clock and a volume a teacher already
            knows how to work, and ones that keep working when she locks her phone. */}
        <audio
          ref={audio}
          controls
          preload="metadata"
          onLoadedMetadata={(event) => { event.currentTarget.playbackRate = speed; }}
          onEnded={() => setPlayingId(null)}
        />
        <div className="recording-player-actions">
          <button type="button" onClick={() => setSpeed(nextSpeed(speed))} aria-label={`Playback speed ${speed} times`}>
            {speed}×
          </button>
          {playing?.hasTranscript && (
            <Link href={`/my-wvcea/learning/${encodeURIComponent(playing.itemId)}`}>Transcript</Link>
          )}
          {playing && (
            <a
              href={audioUrl(guide.event.slug, playing.id)}
              download={`${playing.title.replace(/[^\w\s-]/gu, "").trim() || "recording"}.m4a`}
            >
              Download
            </a>
          )}
        </div>
      </section>

      {recordings === null && <div className="personal-loading" aria-busy="true">Looking for your recordings…</div>}

      {recordings !== null && recordings.length === 0 && (
        <section className="personal-empty">
          <span className="empty-icon">♪</span>
          <p>{failed
            ? "Your recordings could not be loaded. Try again in a moment."
            : "Nothing to hear yet. A session appears here once WVCEA has a recording of it and you were in the room."}</p>
          <Link className="primary-action" href="/schedule">Find a session</Link>
        </section>
      )}

      {byDay.map(([label, items]) => (
        <section className="recording-day" key={label}>
          <h3>
            <span>{label}</span>
            <small>{items.length} {items.length === 1 ? "recording" : "recordings"}</small>
          </h3>
          <ol className="recording-list">
            {items.map((one) => (
              <li key={one.id}>
                <button
                  type="button"
                  className={one.id === playingId ? "playing" : ""}
                  aria-pressed={one.id === playingId}
                  onClick={() => choose(one.id)}
                >
                  <span className="recording-disc" aria-hidden="true">{one.id === playingId ? "❙❙" : "▶"}</span>
                  <span>
                    <strong>{one.title}</strong>
                    <small>{whenLabel(one.startsAt, guide)}{becauseLabel(one.because)}</small>
                  </span>
                  <span className="recording-length">{lengthLabel(one.seconds)}</span>
                </button>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

function audioUrl(slug: string, id: string): string {
  return `/api/attendee/events/${encodeURIComponent(slug)}/recordings/${encodeURIComponent(id)}/audio`;
}

function nextSpeed(current: number): number {
  const at = SPEEDS.indexOf(current as typeof SPEEDS[number]);
  return SPEEDS[(at + 1) % SPEEDS.length] ?? 1;
}

/** "35:42", or "1:05:12" for the long ones. */
export function lengthLabel(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const rest = whole % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${minutes}:${pad(rest)}`;
}

/** Why she has it, said the way she would say it. */
export function becauseLabel(because: readonly string[]): string {
  if (because.includes("recorded")) return " · you recorded this one";
  if (because.includes("checked_in")) return " · you checked in";
  return "";
}

function dayLabel(startsAt: string | null, guide: PublicConventionGuide): string {
  if (!startsAt) return "Your sessions";
  const date = startsAt.slice(0, 10);
  return (guide.days ?? []).find((day) => day.date === date)?.label ?? date;
}

function whenLabel(startsAt: string | null, guide: PublicConventionGuide): string {
  if (!startsAt) return "";
  const time = startsAt.slice(11, 16);
  if (!time) return dayLabel(startsAt, guide);
  const [rawHour, minute] = time.split(":");
  const hour = Number(rawHour);
  const suffix = hour >= 12 ? "p.m." : "a.m.";
  const shown = hour % 12 === 0 ? 12 : hour % 12;
  return `${shown}:${minute} ${suffix}`;
}
